import React from "react";
import * as Sentry from "@sentry/react";

/**
 * Enhanced Error Boundary with Sentry integration
 * This component automatically captures React errors and reports them to Sentry
 * with additional context for better debugging
 */
const SentryErrorBoundary = ({ children, fallback, showDialog = false }) => {
  return (
    <Sentry.ErrorBoundary
      fallback={fallback || ErrorFallback}
      showDialog={showDialog}
      beforeCapture={(scope, error, hint) => {
        // Add additional context before capturing the error
        scope.setTag("errorBoundary", true);
        scope.setContext("errorBoundary", {
          componentStack: hint.componentStack,
          errorBoundary: true,
        });
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};

/**
 * Default fallback component for error boundary
 */
const ErrorFallback = ({ error, resetError }) => {
  return (
    <div
      role="alert"
      className="d-flex flex-column align-items-center justify-content-center p-4"
      style={{ minHeight: "200px" }}
    >
      <h2 className="text-danger mb-3">Something went wrong</h2>
      <p className="text-muted mb-3">
        An error occurred while rendering this component. The error has been
        reported.
      </p>
      {process.env.NODE_ENV === "development" && (
        <details className="mb-3" style={{ whiteSpace: "pre-wrap" }}>
          <summary className="btn btn-outline-secondary btn-sm mb-2">
            Show error details
          </summary>
          {error && error.toString()}
        </details>
      )}
      <button className="btn btn-primary" onClick={resetError}>
        Try again
      </button>
    </div>
  );
};

/**
 * Higher-order component to wrap components with Sentry profiling
 * This creates performance spans for component render cycles
 */
export const withSentryProfiling = (WrappedComponent, componentName) => {
  const ComponentWithProfiling = Sentry.withProfiler(WrappedComponent, {
    name:
      componentName || WrappedComponent.displayName || WrappedComponent.name,
  });

  ComponentWithProfiling.displayName = `withSentryProfiling(${
    componentName || WrappedComponent.displayName || WrappedComponent.name
  })`;

  return ComponentWithProfiling;
};

/**
 * Hook to manually capture exceptions with additional context
 */
export const useSentryCapture = () => {
  const captureException = (error, context = {}) => {
    Sentry.withScope((scope) => {
      Object.keys(context).forEach((key) => {
        scope.setContext(key, context[key]);
      });
      Sentry.captureException(error);
    });
  };

  const captureMessage = (message, level = "info", context = {}) => {
    Sentry.withScope((scope) => {
      Object.keys(context).forEach((key) => {
        scope.setContext(key, context[key]);
      });
      Sentry.captureMessage(message, level);
    });
  };

  const addBreadcrumb = (
    message,
    category = "custom",
    level = "info",
    data = {},
  ) => {
    Sentry.addBreadcrumb({
      message,
      category,
      level,
      data,
    });
  };

  return {
    captureException,
    captureMessage,
    addBreadcrumb,
  };
};

export default SentryErrorBoundary;
